import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Trash2, Plus, Search, Filter, MapPin, Calendar, DollarSign, User, X, ChevronLeft, ChevronRight, PlaneTakeoff, Shield, Clock, Star, Send } from 'lucide-react';
import { tripsAPI, feesAPI, applicationsAPI } from '../services/api';
import CreateTripModal from '../components/CreateTripModal';
import EditTripForm from '../components/EditTripForm';
import TripCardSleek from '../components/TripCardSleek';
import { useThemeColors } from '../theme/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';

const Trips = () => {
  const { user } = useAuth();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [repostData, setRepostData] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);

  const [feeEstimate, setFeeEstimate] = useState(null);
  const [isLoadingFee, setIsLoadingFee] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [bidPrice, setBidPrice] = useState('');
  const [activeTab, setActiveTab] = useState('available');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    origin: '',
    destination: '',
    departureDate: '',
    maxWeight: '',
    status: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Check for repost parameter and open create modal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const newPost = urlParams.get('action') === 'new';

    if (newPost) {
      setShowCreateModal(true);
      // Clear the URL parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // Reset page when filters or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, activeTab]);

  const queryClient = useQueryClient();

  // Fetch available trips with pagination
  const {
    data: availableTripsData,
    isLoading: isLoadingAvailable,
    refetch: refetchAvailable,
  } = useQuery(
    ['availableTrips', currentPage, itemsPerPage, searchTerm, filters],
    () => tripsAPI.getAvailableTrips({
      offset: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage,
      search: searchTerm,
      ...filters
    }),
    {
      enabled: activeTab === 'available',
      onSuccess: (response) => {
        // console.log(response);
        
        setTotalItems(response?.pagination.total || 0);
      },
      select: (response) => {
        // console.log(response.data);
        const apiData = response?.data.data || [];
        return apiData;
      },
    }
  );

  // Fetch user trips with pagination
  const {
    data: userTripsData,
    isLoading: isLoadingUser,
    refetch: refetchUser,
  } = useQuery(
    ['userTrips', currentPage, itemsPerPage, activeTab],
    () => tripsAPI.getUserTrips(activeTab === 'my' ? '' : activeTab, {
      offset: (currentPage - 1) * itemsPerPage,
      limit: itemsPerPage
    }),
    {
      enabled: activeTab !== 'available',
      select: (response) => {
        // If API returns data, use it; otherwise use dummy data
        const apiData = response?.data?.data || [];
        if (apiData.trips.length > 0) {
          return apiData;
        }
        return [];
      },
      onSuccess: (data) => {
        const apiData = data || [];
        if (apiData.pagination?.total) {
          setTotalItems(apiData.pagination.total);
        } else if (apiData.trips?.length) {
          setTotalItems(apiData.trips.length);
        } else {
          setTotalItems(0);
        }
      }
    }
  );

  const availableTrips = availableTripsData?.trips || [];
  const userTrips = userTripsData?.trips || [];
  console.log(availableTrips);
  console.log(userTrips);
  
  
  // Create trip mutation
  const createTripMutation = useMutation(tripsAPI.createTrip, {
    onSuccess: () => {
      toast.success('Trip created successfully!');
      setShowCreateModal(false);
      queryClient.invalidateQueries('userTrips');
      refetchUser();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create trip');
    },
  });

  // Edit trip mutation
  const editTripMutation = useMutation(
    ({ id, data }) => tripsAPI.updateTrip(id, data),
    {
      onSuccess: () => {
        toast.success('Trip updated successfully!');
        setShowEditModal(false);
        queryClient.invalidateQueries('userTrips');
        refetchUser();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update trip');
      },
    }
  );

  // Cancel trip mutation
  const cancelTripMutation = useMutation(tripsAPI.cancelTrip, {
    onSuccess: () => {
      toast.success('Trip cancelled successfully!');
      setShowCancelModal(false);
      queryClient.invalidateQueries('userTrips');
      refetchUser();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to cancel trip');
    },
  });

  // Delete trip mutation (soft delete by updating status)
  const deleteTripMutation = useMutation(
    (id) => tripsAPI.deleteTrip(id), {
    onSuccess: () => {
      toast.success('Trip deleted successfully!');
      queryClient.invalidateQueries('userTrips');
      refetchUser();
      setShowDeleteModal(false);
      setSelectedTrip(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete trip');
    },
  });

  // Apply to trip mutation
  const applyToTripMutation = useMutation(applicationsAPI.createApplication, {
    onSuccess: () => {
      toast.success('Application submitted successfully!');
      setShowApplyModal(false);
      setBidPrice('');
      setFeeEstimate(null);
      queryClient.invalidateQueries('availableTrips');
      refetchAvailable();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to apply for trip');
    },
  });

  const handleCreateTrip = (tripData) => {
    createTripMutation.mutate(tripData);
  };

  const handleEditTrip = (tripData) => {
    editTripMutation.mutate({ id: selectedTrip.id, data: tripData });
  };

  const handleCancelTrip = () => {
    cancelTripMutation.mutate(selectedTrip.id);
  };



  const handleDeleteTrip = () => {
    if (selectedTrip) {
      deleteTripMutation.mutate(selectedTrip.id);
    }
  };


  const handleApplyToTrip = async () => {
    if (!bidPrice || !selectedTrip) return;

    setIsApplying(true);
    try {
      await applyToTripMutation.mutateAsync({
        target_type: 'trip',
        target_id: selectedTrip.id,
        proposal_price: bidPrice,
        message: `I would like to apply for your trip from ${selectedTrip.origin_city} to ${selectedTrip.destination_city}. My proposed price is $${bidPrice}.`
      });
    } finally {
      setIsApplying(false);
    }
  };

  const handleViewDetails = (tripId) => {
    navigate(`/trips/${tripId}`);
  };

  const handleApplyClick = (trip) => {
    setSelectedTrip(trip);
    setShowApplyModal(true);
  };

  const handleEditClick = (trip) => {    
    if (parseInt(trip.applications_count) !== 0) {
      toast.error('Cannot edit trip with applications');
      return;
    }
    setSelectedTrip(trip);
    setShowEditModal(true);
  };

  const handleCancelClick = (trip) => {
    setSelectedTrip(trip);
    setShowCancelModal(true);
  };

  const handleDeleteClick = (trip) => {
    setSelectedTrip(trip);
    setShowDeleteModal(true);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({ ...prev, [filterKey]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      origin: '',
      destination: '',
      departureDate: '',
      maxWeight: '',
      status: '',
      sortBy: 'created_at',
      sortOrder: 'desc'
    });
    setSearchTerm('');
  };

  // Get current trips based on active tab
  const currentTrips = activeTab === 'available' ? availableTrips : userTrips;

  // Ensure currentTrips is always an array
  const tripsToDisplay = Array.isArray(currentTrips) ? currentTrips : [];
  console.log('tripsToDisplay', tripsToDisplay);
  console.log('is array', Array.isArray(tripsToDisplay));
  

  // Get current loading state based on active tab
  const isLoading = activeTab === 'available' ? isLoadingAvailable : isLoadingUser;

  // Check if there are active filters
  const hasActiveFilters = () => {
    const filterFields = ['origin', 'destination', 'departureDate', 'maxWeight', 'status'];
    return filterFields.some(field => filters[field] && filters[field] !== '') || (searchTerm && searchTerm.trim() !== '');
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 overflow-x-hidden">
      <SEO 
        title="Trips" 
        description="Find and manage travel trips for parcel delivery. Browse available trips or post your own travel routes on TripTrot"
        keywords={['travel trips', 'parcel delivery', 'find trips', 'post trips', 'travel routes']}
        canonical="/trips"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Trips",
          "description": "Browse available travel trips and manage trip listings"
        }}
      />
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-theme-primary">Trips</h1>
            <p className="mt-2 text-theme-secondary">
              {activeTab === 'available' 
                ? 'Find trips that match your delivery needs' 
                : 'Manage your trip listings'}
            </p>
          </div>
          {activeTab !== 'available' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-theme-primary-hover transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Post Trip
            </button>
          )}
        </div>
      </div>

      {/* Sleek Tabs */}
      <div className="mb-8">
        {/* Mobile: Vertical tabs | Desktop: Horizontal tabs */}
        <div className="sm:hidden">
          <div 
            className="rounded-2xl p-2 space-y-1 border"
            style={{ 
              backgroundColor: colors.card, 
              borderColor: colors.border 
            }}
          >
            {[
              { key: 'available', label: 'Available', icon: PlaneTakeoff },
              { key: 'my', label: 'My Trips', icon: User },
              // { key: 'posted', label: 'Posted', icon: MapPin },
              // { key: 'cancelled', label: 'Cancelled', icon: X }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center w-full px-3 py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'shadow-lg'
                    : ''
                }`}
                style={{
                  backgroundColor: activeTab === tab.key ? colors.primary[600] : 'transparent',
                  color: activeTab === tab.key ? 'white' : colors.text.secondary
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = colors.neutral[50];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = colors.text.secondary;
                  }
                }}
              >
                <tab.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Desktop version */}
        <div className="hidden sm:block">
          <div 
            className="rounded-2xl p-2 flex flex-wrap gap-2 items-center justify-around"
            style={{ 
              backgroundColor: colors.card, 
              borderColor: colors.border 
            }}
          >
            {[
              { key: 'available', label: 'Available', icon: PlaneTakeoff },
              { key: 'my', label: 'My Trips', icon: User },
              // { key: 'posted', label: 'Posted', icon: MapPin },
              // { key: 'cancelled', label: 'Cancelled', icon: X }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 flex-1 min-w-0 sm:flex-none ${
                  activeTab === tab.key
                    ? 'shadow-lg'
                    : ''
                }`}
                style={{
                  backgroundColor: activeTab === tab.key ? colors.primary[600] : 'transparent',
                  color: activeTab === tab.key ? 'white' : colors.text.secondary
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = colors.neutral[50];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = colors.text.secondary;
                  }
                }}
              >
                <tab.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-8">
        {/* Main Search Card */}
        <div 
          className="rounded-2xl shadow-xl transition-all duration-300 overflow-hidden"
          style={{ 
            backgroundColor: colors.card, 
            borderColor: colors.border 
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colors.primary[300];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = colors.border;
          }}
        >
          {/* Top accent bar */}
          <div className="h-1" />
          
          {/* Content */}
          <div className="p-6">
            {/* Search Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5" 
                  style={{ color: colors.text.secondary }}
                />
                <input
                  type="text"
                  placeholder="Search trips..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 pr-4 py-3 w-full rounded-xl transition-all duration-200 border"
                  style={{
                    backgroundColor: colors.neutral[50],
                    borderColor: colors.border,
                    color: colors.text.primary
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = colors.primary[500];
                    e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = colors.border;
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center justify-center px-6 py-3 rounded-xl transition-all duration-200 font-medium border ${
                  showFilters 
                    ? 'shadow-lg' 
                    : ''
                }`}
                style={{
                  backgroundColor: showFilters ? colors.primary[600] : colors.neutral[50],
                  color: showFilters ? 'white' : colors.text.secondary,
                  borderColor: showFilters ? colors.primary[600] : colors.border
                }}
                onMouseEnter={(e) => {
                  if (!showFilters) {
                    e.currentTarget.style.backgroundColor = colors.neutral[100];
                  }
                }}
                onMouseLeave={(e) => {
                  if (!showFilters) {
                    e.currentTarget.style.backgroundColor = colors.neutral[50];
                  }
                }}
              >
                <Filter className="h-5 w-5 mr-2" />
                Filters
                {(filters.origin || filters.destination || filters.departureDate || filters.maxWeight) && (
                  <span 
                    className="ml-2 px-2 py-0.5 text-xs rounded-full"
                    style={{
                      backgroundColor: colors.primary[600],
                      color: 'white'
                    }}
                  >
                    {[filters.origin, filters.destination, filters.departureDate, filters.maxWeight].filter(Boolean).length}
                  </span>
                )}
              </button>
            </div>

            {/* Filter Row */}
            {showFilters && activeTab === 'available' && (
              <div className="mt-6 pt-6 animate-fadeIn" style={{ borderTop: `1px solid ${colors.border}` }}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  {/* Origin Filter */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Origin city"
                      value={filters.origin}
                      onChange={(e) => setFilters({ ...filters, origin: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl transition-all duration-200 border"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderColor: colors.border,
                        color: colors.text.primary
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = colors.primary[500];
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Destination Filter */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Destination city"
                      value={filters.destination}
                      onChange={(e) => setFilters({ ...filters, destination: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl transition-all duration-200 border"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderColor: colors.border,
                        color: colors.text.primary
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = colors.primary[500];
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Departure Date Filter */}
                  <div className="relative">
                    <input
                      type="date"
                      value={filters.departureDate}
                      onChange={(e) => setFilters({ ...filters, departureDate: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl transition-all duration-200 border"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderColor: colors.border,
                        color: colors.text.primary
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = colors.primary[500];
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>

                  {/* Max Weight Filter */}
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="Max weight (kg)"
                      value={filters.maxWeight}
                      onChange={(e) => setFilters({ ...filters, maxWeight: e.target.value })}
                      min="0"
                      className="w-full px-4 py-2.5 rounded-xl transition-all duration-200 border"
                      style={{
                        backgroundColor: colors.neutral[50],
                        borderColor: colors.border,
                        color: colors.text.primary
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = colors.primary[500];
                        e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary[200]}`;
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = colors.border;
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Trip Cards Grid */}
      {isLoading ? (
        <div className="flex flex-col justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-sm text-gray-500">Loading trips...</p>
        </div>
      ) : tripsToDisplay && tripsToDisplay.length === 0 ? (
        <div className="text-center py-12">
          <PlaneTakeoff className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {hasActiveFilters() ? 'No trips match your filters' : 'No trips found'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {hasActiveFilters() 
              ? 'Try adjusting your search terms or filters to find what you\'re looking for'
              : activeTab === 'available'
                ? 'Check back later for new trips'
                : 'Create your first trip to get started'
            }
          </p>
          {hasActiveFilters() && (
            <div className="mt-4 space-y-2">
              <button
                onClick={() => {
                  setFilters({
                    origin: '',
                    destination: '',
                    departureDate: '',
                    maxWeight: '',
                    status: '',
                    sortBy: 'created_at',
                    sortOrder: 'desc'
                  });
                  setSearchTerm('');
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <X className="h-4 w-4 mr-2" />
                Clear All Filters
              </button>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="ml-2 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear Search
                </button>
              )}
            </div>
          )}
          {activeTab !== 'available' && !hasActiveFilters() && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Trip
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(Array.isArray(tripsToDisplay) ? tripsToDisplay : []).map((trip) => {
            console.log(user.id === trip.traveller_id);
            
            return (
              <TripCardSleek
                key={trip.id}
                trip={trip}
                onApply={handleApplyClick}
                onEdit={handleEditClick}
                onCancel={handleCancelClick}
                onDelete={handleDeleteClick}
                isOwner={user?.id === trip.traveller_id}
                showActions={activeTab === 'my'}
              />
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {(totalItems > 0 || true) && ( // Temporarily always show for testing
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm" style={{ color: colors.text.secondary }}>
            <span>Showing {startItem}-{endItem} of {totalItems} trips</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
              className="px-2 py-1 rounded border text-sm"
              style={{
                borderColor: colors.border,
                backgroundColor: colors.neutral[50],
                color: colors.text.primary
              }}
            >
              <option value={12}>12 per page</option>
              <option value={24}>24 per page</option>
              <option value={48}>48 per page</option>
            </select>
          </div>

          {/* Page navigation - only show if multiple pages */}
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: currentPage === 1 ? colors.text.tertiary : colors.text.secondary,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== 1) {
                    e.currentTarget.style.backgroundColor = colors.neutral[100];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = currentPage === 1 ? colors.text.tertiary : colors.text.secondary;
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>

              {/* Page numbers */}
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'text-white'
                          : 'text-secondary hover:text-primary'
                      }`}
                      style={{
                        backgroundColor: currentPage === pageNum ? colors.primary[600] : 'transparent',
                        color: currentPage === pageNum ? 'white' : colors.text.secondary
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== pageNum) {
                          e.currentTarget.style.backgroundColor = colors.neutral[100];
                          e.currentTarget.style.color = colors.primary[600];
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = currentPage === pageNum ? colors.primary[600] : 'transparent';
                        e.currentTarget.style.color = currentPage === pageNum ? 'white' : colors.text.secondary;
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  color: currentPage === totalPages ? colors.text.tertiary : colors.text.secondary,
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (currentPage !== totalPages) {
                    e.currentTarget.style.backgroundColor = colors.neutral[100];
                    e.currentTarget.style.color = colors.primary[600];
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = currentPage === totalPages ? colors.text.tertiary : colors.text.secondary;
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Create Trip Modal */}
      {showCreateModal && (
        <CreateTripModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateTrip}
          isLoading={createTripMutation.isLoading}
          repostData={repostData}
        />
      )}

      {/* Edit Trip Modal */}
      {showEditModal && selectedTrip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-theme-card rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-theme-light">
              <h2 className="text-xl font-semibold text-theme-primary">Edit Trip</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedTrip(null);
                }}
                className="text-theme-secondary hover:text-theme-primary"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <EditTripForm
                trip={selectedTrip}
                onSubmit={handleEditTrip}
                onCancel={() => {
                  setShowEditModal(false);
                  setSelectedTrip(null);
                }}
                isLoading={editTripMutation.isLoading}
              />
            </div>
          </div>
        </div>
      )}

      {showCancelModal && selectedTrip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-card rounded-lg max-w-md w-full border-theme-light border">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-theme-error rounded-full mb-4">
                <X className="h-6 w-6 text-theme-inverse" />
              </div>
              <h3 className="text-lg font-medium text-theme-primary text-center mb-2">Cancel Trip</h3>
              <p className="text-theme-secondary text-center mb-6">
                Are you sure you want to cancel this trip? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1 px-4 py-2 border border-theme-light rounded-lg text-theme-secondary hover:bg-theme-neutral-50 transition-colors"
                >
                  Keep Trip
                </button>
                <button
                  onClick={handleCancelTrip}
                  disabled={cancelTripMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-theme-error text-white rounded-lg hover:bg-theme-error-hover disabled:opacity-50 transition-colors"
                >
                  {cancelTripMutation.isLoading ? 'Cancelling...' : 'Cancel Trip'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showApplyModal && selectedTrip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-theme-card rounded-lg max-w-2xl w-full border-theme-light border max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-theme-primary">Apply for Trip</h3>
                <button
                  onClick={() => setShowApplyModal(false)}
                  className="text-theme-secondary hover:text-theme-primary transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="mb-6">
                <h4 className="font-medium text-theme-primary mb-2">{selectedTrip.origin_city} → {selectedTrip.destination_city}</h4>
                <p className="text-theme-secondary text-sm">
                  Departure: {new Date(selectedTrip.departure_date).toLocaleDateString()} • 
                  Available Weight: {selectedTrip.available_weight_kg}kg
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-theme-secondary mb-2">Your Bid Price (ETB)</label>
                <input
                  type="number"
                  value={bidPrice}
                  onChange={(e) => setBidPrice(e.target.value)}
                  placeholder="Enter your bid amount"
                  className="w-full px-3 py-2 border border-theme-light rounded-md focus:ring-2 focus:ring-theme-primary focus:border-transparent"
                  style={{ backgroundColor: colors.card }}
                />
              </div>

              {feeEstimate && (
                <div className="mb-6 p-4 rounded-lg border" style={{ backgroundColor: colors.neutral[50], borderColor: colors.border }}>
                  <h5 className="font-medium text-theme-primary mb-3">Fee Breakdown</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Application Fee:</span>
                      <span>{feeEstimate.application_fee} ETB</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Delivery Fee:</span>
                      <span>{feeEstimate.delivery_fee} ETB</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Total Cost:</span>
                      <span>{feeEstimate.total_cost} ETB</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => setShowApplyModal(false)}
                  className="flex-1 px-4 py-2 border border-theme-light rounded-lg text-theme-secondary hover:bg-theme-neutral-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyToTrip}
                  disabled={isApplying || !bidPrice}
                  className="flex-1 px-4 py-2 bg-theme-primary text-white rounded-lg hover:bg-theme-primary-hover disabled:opacity-50 transition-colors"
                >
                  {isApplying ? 'Applying...' : 'Submit Application'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedTrip && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-theme-card rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-theme-error-100 rounded-full flex items-center justify-center">
                <Trash2 className="h-6 w-6 text-theme-error" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-theme-primary">Delete Trip</h3>
                <p className="text-sm text-theme-secondary">
                  Are you sure you want to delete this trip? This action cannot be undone.
                </p>
              </div>
            </div>

            <div className="bg-theme-neutral-50 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-theme-primary capitalize">
                {selectedTrip.category} trip from {selectedTrip.origin_city}
              </p>
              <p className="text-xs text-theme-secondary">
                {selectedTrip.origin_city} → {selectedTrip.destination_city}
              </p>
              <p className="text-xs text-theme-secondary">
                {selectedTrip.weight_kg}kg
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedTrip(null);
                }}
                className="px-4 py-2 text-theme-secondary bg-theme-neutral-100 rounded-md hover:bg-theme-neutral-200"
              >
                Keep Trip
              </button>
              <button
                onClick={handleDeleteTrip}
                disabled={deleteTripMutation.isLoading}
                className="px-4 py-2 bg-theme-error text-theme-inverse rounded-md hover:bg-theme-error-hover disabled:opacity-50"
              >
                {deleteTripMutation.isLoading ? 'Deleting...' : 'Delete Trip'}
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default Trips;
